import websocketHandler from "./websocket-handler";
import { WebsocketMessageType } from "./websocket-message-type";
import { ChatMessage } from "./messages/chat-message";
import { UserUpdateDisplayNameMessage } from "./messages/user-update-display-name-message";
import { ExitRoomMessage } from "./messages/exit-room-message";
import { NewGameMessage } from "./messages/new-game-message";
import { AcceptGameMessage } from "./messages/accept-game-message";
import { CuePositionMessage } from "./messages/cue-position-message";
import { HitPointMessage } from "./messages/hit-point-message";
import { HitDirectionMessage } from "./messages/hit-direction-message";
import { HitCueBallMessage } from "./messages/hit-cue-ball-message";
import { HitResultMessage } from "./messages/hit-result-message";
import { ExitGameMessage } from "./messages/exit-game-message";
import { PassBackMessage } from "./messages/pass-back-message";
import { GameMessage } from "./messages/game-message";
import { TournamentSignupMessage } from "./messages/tournament-signup-message";
import { TournamentAcceptMessage } from "./messages/tournament-accept-message";
import { SynchronizeGameMessage } from "./messages/synchronize-game-message";
import { GameItem } from "../types/game-item";
import { AbstractPlayer } from "../types/abstract-player";
import { HitResultEnum } from "../types/hit-result-enum";
import { GameActionEnum } from "../types/game-action-enum";
import { GameStage } from "../types/game-stage-enum";
import { Vector2 } from "../math/vector2";
import { Player } from "../game/player";

/**
 * The only two pieces of session state any of these messages need in order
 * to be built — read live via callbacks (not captured once) so this stays
 * correct across logins/games without GameApp having to keep this in sync.
 */
export interface GameSocketClientContext {
  getPlayer(): Player;
  getGameStage(): GameStage;
}

/**
 * Every outbound WebSocket message the game sends — extracted verbatim
 * from GameApp, which mixed this in with UI orchestration, session state,
 * and business-logic side effects. GameApp still exposes the same public
 * send*() methods it always did (unchanged for every existing call site
 * across the app, e.g. game.ts, cue-stick.ts, GameChat.vue,
 * InvitationConfirmModal.vue) but now delegates the actual message-
 * building/sending to an instance of this class, keeping only the side
 * effects (winner tracking, credit adjustments, UI reactions, room resets)
 * that don't belong in a network-sending class.
 *
 * `sendHitResultMessage` and `sendExitGameMessage` are intentionally
 * narrower here than GameApp's `sendHitResult`/`sendLeaveGameMessage`: this
 * class only builds and sends the message given already-resolved values
 * (the wager flag, scores, etc.) — GameApp still owns computing those
 * values and reacting to the result.
 */
export class GameSocketClient {
  constructor(private readonly context: GameSocketClientContext) {}

  private get player(): Player {
    return this.context.getPlayer();
  }

  connectWebsocket(playerId: string): void {
    websocketHandler.init(playerId);
  }

  sendChatMessage(msg: string): void {
    msg.trim();

    const message: ChatMessage = {
      msgType: WebsocketMessageType.CHAT_MSG,
      sender: this.player.player,
      playerId: this.player.playerId,
      message: msg,
      gameStage: this.context.getGameStage(),
    };

    if (msg) {
      try {
        websocketHandler.send(message);
      } catch (e) {
        console.error(e);
      }
    }
  }

  updateDisplayName(player: AbstractPlayer): void {
    const message: UserUpdateDisplayNameMessage = {
      msgType: WebsocketMessageType.USER_UPDATE_DISPLAY_NAME,
      sender: this.player.player,
      player,
    };

    try {
      websocketHandler.send(message);
    } catch (e) {
      console.error(e);
    }
  }

  leaveRoom(): void {
    const message: ExitRoomMessage = {
      msgType: WebsocketMessageType.EXIT_ROOM,
      sender: this.player.player,
    };

    try {
      websocketHandler.send(message);
    } catch (e) {
      console.error(e);
    }
  }

  createNewGameRequest(game: GameItem): void {
    const message: NewGameMessage = {
      msgType: WebsocketMessageType.NEW_GAME,
      game,
      sender: this.player.player,
    };

    try {
      websocketHandler.send(message);
    } catch (e) {
      console.error(e);
      // handle the error
    }
  }

  sendAcceptGame(accept: boolean): void {
    const message: AcceptGameMessage = {
      msgType: WebsocketMessageType.ACCEPT_GAME,
      accept,
      sender: this.player.player,
    };

    websocketHandler.send(message);
  }

  sendCueBallPosition(cuePosition: Vector2): void {
    const message: CuePositionMessage = {
      msgType: WebsocketMessageType.CUE_POSITION,
      sender: this.player.player,
      playerId: this.player.playerId,
      positionX: cuePosition.x,
      positionY: cuePosition.y,
    };

    this.sendGameMessage(message);
  }

  sendHitPoint(pointX: number, pointY: number): void {
    const message: HitPointMessage = {
      msgType: WebsocketMessageType.HIT_POINT,
      sender: this.player.player,
      playerId: this.player.playerId,
      pointX: pointX,
      pointY: pointY,
    };

    this.sendGameMessage(message);
  }

  sendHitDirection(direction: Vector2): void {
    const message: HitDirectionMessage = {
      msgType: WebsocketMessageType.HIT_DIRECTION,
      sender: this.player.player,
      playerId: this.player.playerId,
      directionX: direction.x,
      directionY: direction.y,
    };

    this.sendGameMessage(message);
  }

  /**
   * This sends a message to the Java server side as a game message.
   * This is actually just a wrapper around the web socket sender.
   */
  sendGameMessage(message: any): void {
    websocketHandler.send(message);
  }

  sendHitCueBall(direction: number, power: number, hitPosition: Vector2, ballPos: Vector2): void {
    const message: HitCueBallMessage = {
      msgType: WebsocketMessageType.HIT_CUE_BALL,
      sender: this.player.player,
      playerId: this.player.playerId,
      ballPositionX: ballPos.x,
      ballPositionY: ballPos.y,
      // The Java game server expects a Vector for direction but
      // the TypeScript appication sends a rotation (angle) number.
      // To get around this we set the x to the rotation number and the y to 0.
      // This is soley for the game server.
      directionX: direction,
      directionY: 0,
      power,
      hitPointX: hitPosition.x,
      hitPointY: hitPosition.y,
    };

    this.sendGameMessage(message);
  }

  /**
   * Builds and sends the HIT_RESULT message given already-resolved values.
   * See class doc comment — GameApp.sendHitResult() still computes these
   * and still owns the winner-tracking/credit-adjustment side effects.
   */
  sendHitResultMessage(
    result: HitResultEnum,
    currentPlayerIndex: number,
    score1: number,
    score2: number,
    wager: boolean,
    network: string,
  ): void {
    const message: HitResultMessage = {
      msgType: WebsocketMessageType.HIT_RESULT,
      sender: this.player.player,
      result,
      // this has values of 0 and 1 on the server
      currentPlayerIndex,
      // the scores are set here for convenience
      score1,
      score2,
      wager,
      network,
    };

    websocketHandler.send(message);
  }

  /**
   * Builds and sends the EXIT_GAME message given an already-resolved wager
   * flag. See class doc comment — GameApp.sendLeaveGameMessage() still owns
   * the credit adjustment, event firing, and UI/room-reset side effects.
   */
  sendExitGameMessage(wager: boolean, network: string): void {
    const message: ExitGameMessage = {
      msgType: WebsocketMessageType.EXIT_GAME,
      sender: this.player.player,
      wager,
      network,
    };

    websocketHandler.send(message);
  }

  sendUserActionEvent(action: GameActionEnum): void {
    if (action == GameActionEnum.PassBack) {
      const message: PassBackMessage = {
        msgType: WebsocketMessageType.PASS_BACK,
        sender: this.player.player,
      };
      this.sendGameMessage(message);
    } else {
      const message: GameMessage = {
        msgType: WebsocketMessageType.GAME_MESSAGE,
        sender: this.player.player,
        action: action,
      };
      this.sendGameMessage(message);
    }
  }

  sendPassBack(): void {
    const message: PassBackMessage = {
      msgType: WebsocketMessageType.PASS_BACK,
      sender: this.player.player,
    };

    this.sendGameMessage(message);
  }

  sendSignupTournament(link: string): void {
    const message: TournamentSignupMessage = {
      msgType: WebsocketMessageType.TOURNAMNET_SIGNUP,
      tournamentId: link,
      sender: this.player.player,
    };

    try {
      websocketHandler.send(message);
    } catch (e) {
      console.error(e);
    }
  }

  sendAcceptTournament(roomId: string, registered: boolean): void {
    const message: TournamentAcceptMessage = {
      msgType: WebsocketMessageType.TOURNAMENT_ACCEPT,
      tournamentId: roomId,
      sender: this.player.player,
    };

    if (registered) {
      try {
        websocketHandler.send(message);
      } catch (e) {
        console.error(e);
      }
    }
  }

  sendSynchronizeGameMessage(): void {
    const message: SynchronizeGameMessage = {
      msgType: WebsocketMessageType.SYNCHRONIZE_GAME,
      sender: this.player.player,
    };

    try {
      this.sendGameMessage(message);
    } catch (e) {
      console.error(e);
    }
  }
}
